import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { toast } from "sonner";
import { ArrowLeft, Printer, Target, CheckCircle2, Sigma, BookMarked, Lightbulb, Cross, Loader2 } from "lucide-react";

export default function StudyGuide() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const back = user?.role === "Student" ? `/student/lesson/${id}` : `/parent/lesson/${id}`;
  const [g, setG] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/study-guide/${id}`)
      .then((r) => setG(r.data))
      .catch((e) => toast.error(formatError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center text-[#6B7280]"><Loader2 className="animate-spin" /></div>;
  }
  if (!g) {
    return (
      <div>
        <TopBar subtitle="Study Guide" />
        <main className="max-w-3xl mx-auto px-6 py-16 text-center text-[#6B7280]" data-testid="study-guide-empty">Couldn't load this study guide.</main>
      </div>
    );
  }

  const hasContent = (g.key_facts?.length || g.key_concepts?.length || g.vocabulary?.length || g.remember?.length);

  return (
    <div data-testid="study-guide-page">
      <div className="no-print"><TopBar subtitle="Study Guide" /></div>
      <style>{`@media print { .no-print { display:none !important; } body { background:#fff; } .sg-card { break-inside: avoid; } @page { margin: 1.2cm; } }`}</style>

      <main className="max-w-3xl mx-auto px-6 md:px-8 py-10">
        <div className="no-print flex items-center justify-between mb-6">
          <button onClick={() => navigate(back)} className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#3D405B]"><ArrowLeft size={16} /> Back to lesson</button>
          <button data-testid="print-study-guide-btn" onClick={() => window.print()} className="flex items-center gap-2 rounded-full bg-[#3D405B] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#2c2f45] transition-colors"><Printer size={16} /> Print study guide</button>
        </div>

        <header className="mb-8">
          <p className="stat text-xs uppercase tracking-widest text-[#81B29A] font-semibold">{g.subject} · Study Guide</p>
          <h1 className="font-head text-4xl font-bold text-[#3D405B] mt-1" data-testid="study-guide-title">{g.chapter}</h1>
          {g.covers?.length > 0 && (
            <p className="text-[#6B7280] mt-2 leading-relaxed">Covers: <b>{g.covers.join(" · ")}</b></p>
          )}
        </header>

        {!hasContent && (
          <div className="sg-card rounded-2xl border border-[#EAE6DF] bg-white p-6 text-[#6B7280]">No lesson content is available for this chapter yet.</div>
        )}

        {g.objectives?.length > 0 && (
          <section className="sg-card rounded-2xl border border-[#EAE6DF] bg-white p-6 mb-5">
            <h2 className="font-head font-semibold text-[#3D405B] mb-3 flex items-center gap-2"><Target size={18} className="text-[#E07A5F]" /> What you should be able to do</h2>
            <ul className="list-disc pl-5 space-y-1.5 text-sm text-[#3D405B]">
              {g.objectives.map((o, i) => <li key={i}>{o}</li>)}
            </ul>
          </section>
        )}

        {g.key_facts?.length > 0 && (
          <section className="sg-card rounded-2xl border border-[#EAE6DF] bg-white p-6 mb-5">
            <h2 className="font-head font-semibold text-[#3D405B] mb-3 flex items-center gap-2"><CheckCircle2 size={18} className="text-[#F4A261]" /> Key facts to know</h2>
            <ul className="space-y-2">
              {g.key_facts.map((f, i) => (
                <li key={i} className="flex items-start gap-3 rounded-xl bg-[#F4A261]/10 border border-[#F4A261]/30 p-3 text-sm text-[#3D405B]">
                  <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-[#F4A261]" /> {f}
                </li>
              ))}
            </ul>
          </section>
        )}

        {g.key_concepts?.length > 0 && (
          <section className="sg-card rounded-2xl border border-[#A8DADC]/50 bg-[#A8DADC]/10 p-6 mb-5">
            <h2 className="font-head font-semibold text-[#3D405B] mb-3 flex items-center gap-2"><Sigma size={18} className="text-[#457B9D]" /> Formulas & key concepts</h2>
            <ul className="list-disc pl-5 space-y-1.5 text-sm text-[#3D405B]">
              {g.key_concepts.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </section>
        )}

        {g.vocabulary?.length > 0 && (
          <section className="sg-card rounded-2xl border border-[#EAE6DF] bg-white p-6 mb-5">
            <h2 className="font-head font-semibold text-[#3D405B] mb-3 flex items-center gap-2"><BookMarked size={18} className="text-[#81B29A]" /> Vocabulary</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {g.vocabulary.map((v, i) => (
                <div key={i} className="rounded-xl border border-[#EAE6DF] p-3">
                  <p className="font-semibold text-sm text-[#3D405B]">{v.term}</p>
                  <p className="text-xs text-[#6B7280]">{v.definition}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {g.remember?.length > 0 && (
          <section className="sg-card rounded-2xl border border-[#EAE6DF] bg-white p-6 mb-5">
            <h2 className="font-head font-semibold text-[#3D405B] mb-3 flex items-center gap-2"><Lightbulb size={18} className="text-[#E07A5F]" /> Remember these</h2>
            <ul className="list-disc pl-5 space-y-1.5 text-sm text-[#3D405B]">
              {g.remember.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </section>
        )}

        {g.faith?.verse && (
          <section className="sg-card rounded-2xl p-6 text-white grain relative overflow-hidden mb-2" style={{ background: "#3D405B" }}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70 flex items-center gap-2"><Cross size={14} /> Faith Connection</p>
            {g.faith.reflection && <p className="mt-2 leading-relaxed text-white/95 text-sm">{g.faith.reflection}</p>}
            <p className="mt-3 italic text-white/90">"{g.faith.verse}" <span className="stat not-italic text-white/70">— {g.faith.reference}</span></p>
          </section>
        )}
      </main>
    </div>
  );
}
